import React, { useEffect, useState, useRef } from 'react';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SidebarMain from '../../sidebars/SidebarMain';
import { useLikes, likeKeyFromRow } from '../../../shared/contexts/LikesContext';
import { PostSection } from "./MyLikesUIFunctions";
import type { MediaEntry, Post } from "./MyLikesUIFunctions";
import { useTranslation } from 'react-i18next';
import GalleryPresentation from "../Gallery/GalleryPresentation";
import { formatMediaArray } from "./normalizeMediaForMyLikes";
import type {
  Post as GUIPost,
  MediaEntry as GUIMediaEntry,
} from "../Gallery/GalleryUIFunctions";


// Api base URL normalization

const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL!.replace(/\/$/, "");

function normalizeImageUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (/^(https?:|data:)/i.test(url)) return url;
  return `${IMAGE_CDN}/${url.replace(/^\//, "")}`;
}

const MAX_POSTS_IN_MEMORY = 180;

const MyLikes = () => {
  const [likedPosts, setLikedPosts] = useState<Post[]>([]);
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useTranslation();

  const LIMIT = 100;
  const [isFetching, setIsFetching] = useState(false);
  const [globalMuted] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);

  const isFetchingRef = React.useRef(false);
  const offsetRef = React.useRef(0);
  const feedRef = useRef<HTMLDivElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { unlikePost, refreshLikes } = useLikes();

  const [initialMediaReady, setInitialMediaReady] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const hasMoreRef = useRef(true);


  //Presentation
  const [presentationPost, setPresentationPost] = useState<GUIPost | null>(null);
  const [presentationIndex, setPresentationIndex] = useState<number | null>(null);


  
  const handleOpenMusic = async (entry: MediaEntry) => {
    // 1️⃣ If we already know the track id, go straight to details
    if (entry.musicId) {
      navigate(`/musicdetails/${entry.musicId}`);
      return;
    }
  
    // 2️⃣ Fallback: search by either title OR artist (not "title artist")
    const q = (entry.musicTitle || entry.musicArtist || '').trim();
    if (!q) {
      console.warn('[MyLikes.handleOpenMusic] no title/artist on entry', entry);
      return;
    }
  
    try {
      const res = await api.get('/music', { params: { q } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
      if (!list.length) {
        console.warn('[MyLikes.handleOpenMusic] no music match for', q);
        return;
      }
  
      navigate(`/musicdetails/${list[0]._id}`);
    } catch (e) {
      console.error('[MyLikes.handleOpenMusic] music lookup failed:', e);
    }
  };
  
 
const toGUIMediaEntry = (m: MediaEntry): GUIMediaEntry => ({
  url: m.isHls ? m.url : normalizeImageUrl(m.url),
  isHls: m.isHls,
  videoId: m.videoId,
  hlsUrl: m.hlsUrl || undefined,
  dashUrl: m.dashUrl || undefined,
  musicId: m.musicId || undefined,
  musicTitle: m.musicTitle || undefined,
  musicArtist: m.musicArtist || undefined,
});


const toGUIPost = (p: Post): GUIPost => ({
  _id: p._id,
  media: p.media.map(toGUIMediaEntry), 
  captions: p.captions ?? new Array(p.media.length).fill(""),
  createdAt: p.createdAt,
  likes: p.likes,
  likedByUser: p.likedByUser ?? true,
  location: p.location,
  user: p.user,
  totalLikes: p.totalLikes,
  likeKey: p.likeKey,
  hashtags: p.hashtags ?? [],
  status: p.status,
});



  const openPresentation = (postId: string) => {
  const p = likedPosts.find(x => x._id === postId);
  if (!p || !p.media.length) return;

  setPresentationPost(toGUIPost(p));
  setPresentationIndex(0);
};

const openPresentationAt = (post: Post, index: number) => {
  setPresentationPost(toGUIPost(post));
  setPresentationIndex(index);
};

  // ---- SEARCH HELPERS for My Likes (hashtags, captions, location, etc.) ----
  const normText = (s?: string | null) => (s || '').toLowerCase();

  const matchesSearch = (post: Post, rawQuery: string): boolean => {
    const q = rawQuery.trim();
    if (!q) return true; // empty search → no filtering

    // remove leading '#' so both "#budapest" and "budapest" work
    const needle = q.replace(/^#/, '').toLowerCase();

    const haystack: string[] = [];
    const anyPost: any = post;

    // Text fields if present
    if (anyPost.caption) haystack.push(anyPost.caption);
    if (anyPost.description) haystack.push(anyPost.description);
    if (anyPost.title) haystack.push(anyPost.title);

    // Hashtags / tags arrays
    if (Array.isArray(anyPost.hashtags)) haystack.push(...anyPost.hashtags);
    if (Array.isArray(anyPost.tags)) haystack.push(...anyPost.tags);

    // Location fields
    if (post.location?.city) haystack.push(post.location.city);
    if (post.location?.country) haystack.push(post.location.country);

    // Also allow searching by author's profilename
    if (post.user?.profilename) haystack.push(post.user.profilename);

    // Substring, case-insensitive
    return haystack.some((txt) => txt && normText(txt).includes(needle));
  };

  const filteredLikedPosts = React.useMemo(
    () => likedPosts.filter((p) => matchesSearch(p, searchQuery)),
    [likedPosts, searchQuery]
  );

  
  // Fetch liked posts
  const fetchLikedPosts = async () => {
if (!hasMoreRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);

    try {
      const start = offsetRef.current;
      const res = await api.get('/likes/liked/mine', {
        params: { offset: start, limit: LIMIT },
      });

      const newPostsRaw: any[] = res.data || [];

      console.groupCollapsed('%c[MyLikes] RAW /likes/liked/mine response', 'color:#00e5ff');
newPostsRaw.forEach((p, i) => {
  console.log(`Post ${i}`, {
    id: p._id,
    media: p.media,
  });
});
console.groupEnd();



      const newPosts: Post[] = newPostsRaw.map((p: any) => {
      const media = formatMediaArray(p.media);

      console.groupCollapsed(`%c📸 Media for post ${p._id}`, 'color:#2979ff');
  console.log('Raw media:', p.media);
  console.log('Normalized media:', media);
  console.groupEnd();
  
  // 🔑 canonical like id for this row (same logic as in LikesContext)
  const likeKey = likeKeyFromRow(p);

  const likesCount =
    typeof p.likes === 'number'
      ? p.likes
      : typeof p.totalLikes === 'number'
      ? p.totalLikes
      : 0;

  return {
    ...p,
    _id: likeKey,                 // 👈 use canonical key as primary id
    likeKey,                      // (optional: handy for debugging)
    media,
    captions: new Array(media.length).fill(""),
    likes: likesCount,
    likedByUser: true,            // this list *only* contains liked posts
  } as Post;
});

console.groupCollapsed('%c[MyLikes] NORMALIZED POSTS', 'color:#ff9100');
newPosts.forEach((p, i) => {
  console.log(`Post ${i}`, {
    id: p._id,
    media: p.media.map(m => ({
      isHls: m.isHls,
      videoId: m.videoId,
      url: m.url,
      type: m.isHls ? 'VIDEO' : 'IMAGE',
    })),
  });
});
console.groupEnd();


  setLikedPosts(prev => {
  const merged = [...prev, ...newPosts];
  const byId = new Map(merged.map(p => [p._id, p]));
  return Array.from(byId.values()).slice(-MAX_POSTS_IN_MEMORY);
});

      if (newPosts.length < LIMIT) {
  hasMoreRef.current = false;
}


      offsetRef.current = start + newPosts.length;
    } catch (err) {
      console.error('❌ Failed to fetch liked posts:', err);
    } finally {
  isFetchingRef.current = false;
  setIsFetching(false);
  setInitialFetchDone(true);
}
  };


  useEffect(() => {
  if (!likedPosts.length) {
    setInitialMediaReady(false);
    return;
  }

  const firstPost = likedPosts[0];
  const media = firstPost?.media?.[0];

  let cancelled = false;

  const finish = () => {
    if (cancelled) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) {
          setInitialMediaReady(true);
        }
      });
    });
  };

  if (!media) {
    finish();

    return () => {
      cancelled = true;
    };
  }

  // IMAGE
  if (!media.isHls && media.url) {
    const img = new Image();
    img.src = media.url;

    const ready = async () => {
      try {
        await img.decode();
      } catch {}

      finish();
    };

    if (img.complete) {
      ready();
    } else {
      img.onload = ready;
      img.onerror = finish;
    }

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }

  // VIDEO POSTER
  if (media.posterUrl) {
    const poster = new Image();
    poster.src = media.posterUrl;

    const ready = async () => {
      try {
        await poster.decode();
      } catch {}

      finish();
    };

    if (poster.complete) {
      ready();
    } else {
      poster.onload = ready;
      poster.onerror = finish;
    }

    return () => {
      cancelled = true;
      poster.onload = null;
      poster.onerror = null;
    };
  }

  // VIDEO WITHOUT POSTER
  finish();

  return () => {
    cancelled = true;
  };
}, [likedPosts]);


  useEffect(() => {
    refreshLikes();
  }, [refreshLikes]);

  useEffect(() => {
  if (!token) return;

  setLikedPosts([]);
  setInitialMediaReady(false);
  setInitialFetchDone(false);

  hasMoreRef.current = true;
  offsetRef.current = 0;
  isFetchingRef.current = false;
  setIsFetching(false);

  fetchLikedPosts();
}, [token]);


  // Only one Video at a time
  useEffect(() => {
    const onPlay = (e: Event) => {
      const el = e.target as HTMLVideoElement | null;
      if (!el || el.tagName !== 'VIDEO') return;

      document.querySelectorAll('video').forEach((v) => {
        if (v !== el) {
          try { v.pause(); } catch {}
        }
      });
    };

    document.addEventListener('play', onPlay, true);
    return () => document.removeEventListener('play', onPlay, true);
  }, []);



  // Unlike handler
  const removeLike = async (post: Post) => {
    const prev = likedPosts;
    setLikedPosts(p => p.filter(x => x._id !== post._id));

    try {
      await unlikePost(post._id);
    } catch (err) {
      console.error('❌ Failed to remove like:', err);
      setLikedPosts(prev); // rollback on error
    }
  };

  
  //Loading
  useEffect(() => {
  const styleId = 'spin-keyframes';

  // prevent injecting it multiple times (important!)
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  document.head.appendChild(style);

  return () => {
    style.remove();
  };
}, []);


useEffect(() => {
  const start = Math.max(0, pageIndex - 1);
  const end = Math.min(filteredLikedPosts.length, pageIndex + 3);

  for (let i = start; i < end; i++) {
    const post = filteredLikedPosts[i];
    post?.media?.forEach(m => {
      if (!m.isHls && m.url) {
        const img = new Image();
        img.src = m.url;
      }
    });
  }
}, [pageIndex, filteredLikedPosts]);


 useEffect(() => {
  const handler = (e: KeyboardEvent) => {
    // ignore if typing in inputs
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();

      setPageIndex((prev) => {
        const next = Math.min(prev + 1, filteredLikedPosts.length - 1);

        feedRef.current?.scrollTo({
          top: next * (feedRef.current.clientHeight || window.innerHeight),
          behavior: 'smooth',
        });

        return next;
      });
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();

      setPageIndex((prev) => {
        const next = Math.max(prev - 1, 0);

        feedRef.current?.scrollTo({
          top: next * (feedRef.current.clientHeight || window.innerHeight),
          behavior: 'smooth',
        });

        return next;
      });
    }
  };

  window.addEventListener('keydown', handler);
  return () => window.removeEventListener('keydown', handler);
}, [filteredLikedPosts.length]);


useEffect(() => {
  setPageIndex(prev => Math.min(prev, Math.max(0, filteredLikedPosts.length - 1)));
}, [filteredLikedPosts.length]);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <SidebarMain
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />     

      {/* Left bottom terms privacy help buttons */}
<div
  style={{
    position: "fixed",
    left: 18,
    bottom: 8,
    width: 230,
    zIndex: 50,
    fontSize: 10,
    color: "#555",
    lineHeight: 1.6,
    textAlign: "left",
    pointerEvents: "auto",
  }}
>
  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/helpsupportstart")}
  >
    {t("mainSidebar.footer.helpSupport")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/privacypolicy")}
  >
    {t("mainSidebar.footer.privacyPolicy")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/termsofuse")}
  >
    {t("mainSidebar.footer.termsOfUse")}
  </span>
</div>
 

      {/* Feed */}
<div
  ref={feedRef}
  onScroll={(e) => {
    const el = e.currentTarget as HTMLDivElement;
    const idx = Math.round((el.scrollTop || 0) / (el.clientHeight || 1));
    if (idx !== pageIndex) setPageIndex(idx);
  }}
 style={{
  ...feedContainerStyle,
  position: 'relative',
  padding: '7px 7px 0 7px',
  height: '100vh',
  scrollSnapType: 'y mandatory',
  touchAction: 'pan-y',
  overscrollBehaviorY: 'contain',
}}
>

{filteredLikedPosts.length === 0 && !isFetching ? (
  <p style={{ color: '#bbb', margin: '40px 0 0 8px' }}>
    💔 {t('MyLikes.empty')}
  </p>
) : (
  filteredLikedPosts.map((post, idx) => {
    const isActive = idx === pageIndex;
    const inWindow = idx >= pageIndex - 1 && idx <= pageIndex + 1;

    if (!inWindow) {
      return (
        <section
          key={post._id}
          style={{
            minHeight: '100vh',
            height: '100vh',
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
          }}
        />
      );
    }

    return (
      <section
        key={post._id}
        data-post-id={post._id}
        style={{
          minHeight: '100vh',
          height: '100vh',
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
        }}
      >
        <PostSection
          post={post}
          isActive={isActive}
          inWindow={inWindow}
          globalMuted={globalMuted}
          openViewer={(media, i) => openPresentationAt(post, i)}
          openPresentation={openPresentation}
          navigate={navigate}
          onLike={() => {}}
          onUnlike={(id) => {
            const found = likedPosts.find((p) => p._id === id);
            if (found) removeLike(found);
          }}
          currentUserId={user?._id}
          onMusicClick={handleOpenMusic}
        />
      </section>
    );
  })
)}

{!initialMediaReady &&
  !(initialFetchDone && likedPosts.length === 0) && (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: 100,
      backgroundColor: '#000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 12,
      color: '#bbb',
      fontSize: 18,
      letterSpacing: 0.4,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #fff',
        animation: 'spin 0.9s linear infinite',
      }}
    />

    <div style={{ opacity: 0.9 }}>
      {t('MyLikes.loading')}
    </div>
  </div>
)}
      </div>

{presentationPost && presentationIndex !== null && (
  <GalleryPresentation
    post={presentationPost}
    startIndex={presentationIndex}
    onClose={() => {
      setPresentationPost(null);
      setPresentationIndex(null);
    }}
  />
)}
    </div>
  );
};

// Styles

const SIDEBAR_WIDTH = 270;

const feedContainerStyle: React.CSSProperties = {
  flexGrow: 1,
  padding: '0px 20px',
  overflowY: 'auto',
  height: '100vh',
  minHeight: '100%',
  marginLeft: `${SIDEBAR_WIDTH}px`,
  backgroundColor: '#000',
  border: 'none',
  touchAction: 'pan-y',
  overscrollBehaviorY: 'contain',
  overscrollBehaviorX: 'none',
  overflowAnchor: 'none',
};

export default MyLikes;     
